import { useState } from "react";
import { FileSpreadsheet, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const ALL_ROLES = ["super_admin", "sales_head", "management"];

/** Downloads the user's own leads / opportunities as Excel; elevated roles can also export everything visible. */
export function ExportButton({ entity, path, divisionParam }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const label = entity === "lead" ? "leads" : "opportunities";

  const download = async (scope) => {
    setBusy(true);
    try {
      const res = await api.get(path, { params: { scope, division_id: divisionParam }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}_${scope === "all" ? "all" : "my"}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${scope === "all" ? "all visible" : "your"} ${label}`);
    } catch (e) {
      let msg = apiError(e);
      if (e?.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep */ } }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const icon = busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />;
  if (!ALL_ROLES.includes(user?.role)) {
    return (
      <Button variant="outline" onClick={() => download("mine")} disabled={busy} className="gap-1.5" data-testid={`${entity}-export-btn`}>
        {icon} Export
      </Button>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={busy} className="gap-1.5" data-testid={`${entity}-export-btn`}>{icon} Export <ChevronDown className="h-3.5 w-3.5 opacity-60" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => download("mine")} data-testid={`${entity}-export-mine`}>My {label} only</DropdownMenuItem>
        <DropdownMenuItem onClick={() => download("all")} data-testid={`${entity}-export-all`}>All {label} I can see</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
